import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, HERITAGE_ASSETS } from '@/lib/api';
import { Panel, SectionDivider } from '@/components/Panel';
import { HeritageSeal, DahliaMark } from '@/components/BrandMark';
import { ArrowRight, Feather, Flower2, Sparkles, Shield } from 'lucide-react';

export default function HomePage() {
  const [content, setContent] = useState({});
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, t] = await Promise.all([api.get('/site-content'), api.get('/tiers')]);
        setContent(c.data.content || {});
        setTiers(t.data.tiers || []);
      } catch (err) { console.warn('[HomePage] initial fetch failed', err); }
    })();
  }, []);

  return (
    <div>
      {/* HERO — render the full banner image so both the HERITAGE APPAREL text at top AND the hoodies are visible on every screen size */}
      <section className="relative">
        <div className="hero-banner-frame relative" data-testid="hero-banner">
          <img
            src={HERITAGE_ASSETS.heroBanner}
            alt="Heritage Apparel banner"
            className="hero-banner-img"
          />
          <div className="absolute inset-x-0 bottom-4 md:bottom-8 flex justify-center z-10 px-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/store" className="h-btn-pinkle" data-testid="hero-cta-shop">
                {content.hero_cta_primary || 'Shop the Store'} <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link to="/catalog" className="h-btn-outline" style={{ background: 'rgba(246,240,228,0.9)' }}>{content.hero_cta_secondary || 'Explore the Catalogue'}</Link>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-12 pb-6 text-center">
          <div className="h-eyebrow mb-4">Est. Aotearoa. A dahlia field label</div>
          <h1 className="h-display text-4xl md:text-6xl leading-[1.05] mb-4" style={{ color: 'var(--heritage-plum)' }}>
            {content.hero_headline || 'Grown in the field. Sewn with intention.'}
          </h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg leading-relaxed opacity-85" style={{ color: 'var(--heritage-plum)' }}>
            {content.hero_sub || 'Heritage Apparel is a small batch, dahlia inspired label from Aotearoa. Every garment is numbered, embroidered, and made to be worn for a decade.'}
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full" style={{ background: '#9A4B7E' }} />
              <div className="text-left">
                <div className="h-eyebrow">No. 110</div>
                <div className="text-sm h-serif" style={{ color: 'var(--heritage-plum)' }}>Pinkle</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full" style={{ background: '#8FA34B' }} />
              <div className="text-left">
                <div className="h-eyebrow">No. 116</div>
                <div className="text-sm h-serif" style={{ color: 'var(--heritage-plum)' }}>F**ken Green</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider label="The Heritage Story" />

      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: <Flower2 size={26} />, title: 'Dahlia Grown', body: 'Every colourway begins in the dahlia field. The palette is dictated by petals, not trends.' },
            { icon: <Feather size={26} />, title: 'Hand Embroidered', body: 'Each Heritage crown motif takes over 4,200 stitches. Slow work, made to last.' },
            { icon: <Sparkles size={26} />, title: 'Numbered Editions', body: 'Founders receive a lifetime number engraved into the garment tag. Never reissued.' },
          ].map((c) => (
            <Panel key={c.title} className="p-8" data-testid={`story-${c.title.replace(' ', '-')}`}>
              <div className="mb-4" style={{ color: 'var(--heritage-plum)' }}>{c.icon}</div>
              <div className="h-serif text-2xl mb-2" style={{ color: 'var(--heritage-plum)' }}>{c.title}</div>
              <p className="text-sm leading-relaxed opacity-80" style={{ color: 'var(--heritage-plum)' }}>{c.body}</p>
            </Panel>
          ))}
        </div>
      </section>

      <SectionDivider label="Signature Colours" />

      <section className="max-w-6xl mx-auto px-6 py-10">
        <Panel className="p-3">
          <img src={HERITAGE_ASSETS.swatches} alt="Signature Colours" className="w-full rounded-xl" />
        </Panel>
      </section>

      <SectionDivider label={content.presale_eyebrow || 'Presale Open Now'} />

      <section className="max-w-6xl mx-auto px-6 py-10">
        <Panel className="p-8 md:p-12 text-center" data-testid="home-presale-banner">
          <div className="h-eyebrow">{content.presale_eyebrow || 'Presale Open Now'}</div>
          <div className="h-display text-3xl md:text-5xl mt-2" style={{ color: 'var(--heritage-plum)' }}>
            {content.presale_headline || 'Heritage No. 1 Collection'}
          </div>
          <p className="max-w-2xl mx-auto text-sm md:text-base opacity-85 mt-3 leading-relaxed" style={{ color: 'var(--heritage-plum)' }}>
            {content.presale_body || 'Reserve your Heritage piece before the collection ships. Limited quantities, numbered garments, made in Aotearoa.'}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to={content.presale_cta_url || '/store'} className="h-btn" data-testid="home-presale-cta">
              {content.presale_cta_label || 'Shop the Presale'} <ArrowRight size={16} className="ml-2" />
            </Link>
            <Link to="/catalog" className="h-btn-outline">See the full Catalogue</Link>
          </div>
        </Panel>
      </section>

      {/* Admin access strip at bottom of homepage */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-6 px-6 rounded-2xl"
             style={{ background: 'rgba(75,24,56,0.06)', border: '1.5px solid rgba(75,24,56,0.15)' }}>
          <div className="flex items-center gap-3">
            <Shield size={20} style={{ color: 'var(--heritage-plum)' }} />
            <div>
              <div className="h-eyebrow">Team access</div>
              <div className="text-sm" style={{ color: 'var(--heritage-plum)' }}>Heritage staff and administrators.</div>
            </div>
          </div>
          <Link to="/login?admin=1" className="h-btn-outline text-xs" data-testid="home-admin-login">
            <Shield size={14} className="mr-2" /> Admin Login
          </Link>
        </div>
      </section>

      {/* ---------- TrustBuy footer callout ---------- */}
      <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="home-trustbuy">
        <div className="rounded-2xl overflow-hidden" style={{ background: '#FBF8F1', border: '1.5px solid rgba(75,24,56,0.2)' }}>
          <div className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1">
              <div className="h-eyebrow">TrustBuy &mdash; your Heritage Tab</div>
              <div className="h-serif text-2xl md:text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Buy with trust. Pay your way.</div>
              <p className="text-sm mt-2 opacity-85 leading-relaxed max-w-2xl" style={{ color: 'var(--heritage-plum)' }}>
                No credit checks. No hidden fees. No debt collectors. Just honest people, honest payments. Choose your Heritage order, pay a small deposit, and split the balance across a few weeks. Your order ships once the final payment is received.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs opacity-80" style={{ color: 'var(--heritage-plum)' }}>
                <span>&#10003; No credit checks</span>
                <span>&#10003; No interest</span>
                <span>&#10003; No late fees</span>
                <span>&#10003; Life-happens pause available</span>
              </div>
            </div>
            <Link to="/trustbuy" className="h-btn-outline whitespace-nowrap" data-testid="home-trustbuy-cta">
              Learn how it works
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
